import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DonorSearch from './DonorSearch';
import Navbar from '../../components/Navbar';

const ANY = "Any";

const mockDonors = [
  { id: 1, donor_code: "1234", height_cm: 178, weight_kg: 79, eye_color: "Blue", hair_color: "Brown", ethnicity: "Caucasian", blood_type: "O+", education: "PhD Candidate", occupation: "Research Scientist", personality: "Analytical and curious", interests: ["Science", "Reading", "Hiking"], bio: "Passionate about genetic research and helping families grow.", available: true },
  { id: 2, donor_code: "5678", height_cm: 173, weight_kg: 73, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "A+", education: "MD", occupation: "Physician", personality: "Compassionate and dedicated", interests: ["Medicine", "Music", "Travel"], bio: "Dedicated doctor wanting to help others build families.", available: true },
  { id: 3, donor_code: "9012", height_cm: 183, weight_kg: 82, eye_color: "Brown", hair_color: "Black", ethnicity: "Hispanic", blood_type: "B+", education: "BS Engineering", occupation: "Engineer", personality: "Creative problem-solver", interests: ["Technology", "Sports", "Cooking"], bio: "Engineer with a passion for innovation and helping others.", available: true },
  { id: 4, donor_code: "3456", height_cm: 180, weight_kg: 84, eye_color: "Brown", hair_color: "Black", ethnicity: "African American", blood_type: "O-", education: "MBA", occupation: "Business Analyst", personality: "Strategic thinker", interests: ["Finance", "Fitness", "Photography"], bio: "Business professional focused on strategic planning and community.", available: false },
  { id: 5, donor_code: "7890", height_cm: 175, weight_kg: 77, eye_color: "Green", hair_color: "Blond", ethnicity: "Caucasian", blood_type: "A-", education: "MS Biology", occupation: "Biologist", personality: "Detail-oriented", interests: ["Nature", "Research", "Cycling"], bio: "Biologist studying ecosystems and conservation.", available: true },
  { id: 6, donor_code: "2345", height_cm: 170, weight_kg: 70, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "AB+", education: "PhD Physics", occupation: "Physicist", personality: "Analytical and precise", interests: ["Physics", "Chess", "Running"], bio: "Physicist exploring the fundamental laws of the universe.", available: true },
];

function unique(list) {
  return Array.from(new Set(list)).sort();
}

function Field({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border/60 pb-1">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function DonorCard({ d }) {
  return (
    <Link
      to={`/donors/${d.id}`}
      className="group rounded-2xl border border-border bg-card overflow-hidden flex flex-col hover:shadow-lg hover:border-primary/40 transition"
      onClick={() => console.log('Clicked donor:', d.donor_code)}
    >
      <div className="aspect-[4/3] bg-secondary flex items-center justify-center">
        <span className="text-6xl text-primary/30 font-display">{d.donor_code.charAt(0)}</span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl">{d.donor_code}</div>
          <span className={`text-xs rounded-full px-2 py-1 ${d.available ? 'bg-accent' : 'bg-muted'}`}>{d.available ? "Available" : "Reserved"}</span>
        </div>
        <div className="mt-1 text-sm text-muted-foreground">{d.occupation} · {d.education}</div>

        <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <Field k="Ethnicity" v={d.ethnicity} />
          <Field k="Blood" v={d.blood_type} />
          <Field k="Eyes" v={d.eye_color} />
          <Field k="Hair" v={d.hair_color} />
          <Field k="Height" v={`${d.height_cm} cm`} />
          <Field k="Weight" v={`${d.weight_kg} kg`} />
        </div>

        <p className="mt-4 text-sm text-foreground/80 line-clamp-3">{d.bio}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {d.interests.slice(0, 3).map((i) => (
            <span key={i} className="text-xs rounded-full bg-secondary px-2.5 py-1 text-secondary-foreground">{i}</span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function Donors() {
  const [ethnicity, setEthnicity] = useState(ANY);
  const [eye, setEye] = useState(ANY);
  const [hair, setHair] = useState(ANY);
  const [education, setEducation] = useState(ANY);
  const [blood, setBlood] = useState(ANY);
  const [minHeight, setMinHeight] = useState(160);
  const [query, setQuery] = useState("");

  const options = useMemo(() => {
    return {
      ethnicity: unique(mockDonors.map((d) => d.ethnicity)),
      eye: unique(mockDonors.map((d) => d.eye_color)),
      hair: unique(mockDonors.map((d) => d.hair_color)),
      education: unique(mockDonors.map((d) => d.education)),
      blood: unique(mockDonors.map((d) => d.blood_type)),
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mockDonors.filter((d) => {
      if (ethnicity !== ANY && d.ethnicity !== ethnicity) return false;
      if (eye !== ANY && d.eye_color !== eye) return false;
      if (hair !== ANY && d.hair_color !== hair) return false;
      if (education !== ANY && d.education !== education) return false;
      if (blood !== ANY && d.blood_type !== blood) return false;
      if (d.height_cm < minHeight) return false;
      if (q) {
        const hay = `${d.donor_code} ${d.occupation} ${d.personality} ${d.interests.join(" ")} ${d.bio}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [ethnicity, eye, hair, education, blood, minHeight, query]);

  const resetAll = () => {
    setEthnicity(ANY);
    setEye(ANY);
    setHair(ANY);
    setEducation(ANY);
    setBlood(ANY);
    setMinHeight(160);
    setQuery("");
    console.log('Filters reset');
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Donors Page */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-4xl sm:text-6xl">Find a donor</h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Every profile is physician-screened. Apply for a donor-funded subsidy directly on any profile.
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            {filtered.length} of {mockDonors.length} donors
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Filters */}
          <DonorSearch
            query={query}
            setQuery={setQuery}
            ethnicity={ethnicity}
            setEthnicity={setEthnicity}
            eye={eye}
            setEye={setEye}
            hair={hair}
            setHair={setHair}
            education={education}
            setEducation={setEducation}
            blood={blood}
            setBlood={setBlood}
            minHeight={minHeight}
            setMinHeight={setMinHeight}
            resetAll={resetAll}
            options={options}
          />

          {/* Results */}
          <div>
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border p-16 text-center">
                <div className="font-display text-2xl">No matches</div>
                <p className="mt-2 text-sm text-muted-foreground">Try loosening a filter or two.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((d) => <DonorCard key={d.id} d={d} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row gap-2 justify-between">
          <div>© {new Date().getFullYear()} The Open Sperm Bank, LLC</div>
          <div>FDA registered · Physician-supervised · HIPAA</div>
        </div>
      </footer>
    </div>
  );
}

export default Donors;
